import LegalShell from "./LegalShell";

const Section = ({ title, children }) => (
  <section>
    <h2 className="font-display text-white text-xl md:text-2xl tracking-tight mb-3">{title}</h2>
    <div className="space-y-3 text-zinc-400">{children}</div>
  </section>
);

const Privacy = () => {
  return (
    <LegalShell
      testid="privacy-page"
      eyebrow="Legal · Privacy"
      title="Privacy Policy"
      updated="December 2025"
    >
      <p className="text-zinc-400">
        Equal is an accessibility-first company. This policy explains what we collect, why we
        collect it, and the choices you have. If you don't want to read the whole thing: we collect
        as little as possible, never sell your data, and work hard to keep what we do collect safe.
      </p>

      <Section title="1. Who is responsible">
        <p>
          Equal (“we”, “us”, “our”) is the data controller for information collected through
          <span className="text-white"> equallayer.app</span> and the EqualLayer Service. Contact:{" "}
          <a className="text-indigo-300 hover:text-indigo-200" href="mailto:privacy@equallayer.app">
            privacy@equallayer.app
          </a>
          .
        </p>
      </Section>

      <Section title="2. What we collect">
        <ul className="list-disc list-outside pl-5 space-y-2">
          <li>
            <span className="text-white">Waitlist email</span> — when you join early access, we
            store the email address you submit (via Formspree) so we can contact you about Equal.
          </li>
          <li>
            <span className="text-white">Basic analytics</span> — we may collect anonymized
            information about pages visited, referrer, device type, and approximate location, so we
            can improve accessibility and performance.
          </li>
          <li>
            <span className="text-white">Cookies</span> — a small number of essential cookies keep
            the site working; optional analytics cookies are only set if you consent.
          </li>
        </ul>
        <p>
          We do <span className="text-white">not</span> collect payment information, government IDs,
          or sensitive personal data on this site.
        </p>
      </Section>

      <Section title="3. Why we collect it">
        <ul className="list-disc list-outside pl-5 space-y-2">
          <li>To send early-access invitations and product updates you've asked to receive.</li>
          <li>To understand how people use the site so we can prioritize accessibility work.</li>
          <li>To keep the Service secure and prevent abuse.</li>
        </ul>
      </Section>

      <Section title="4. How we share it">
        <p>
          We share information only with trusted service providers that help us run Equal — for
          example, our waitlist provider (Formspree) and our hosting partner. These providers are
          contractually limited to using the data only to provide their service to us.
        </p>
        <p>
          We will <span className="text-white">never</span> sell your personal information.
        </p>
      </Section>

      <Section title="5. Your rights">
        <p>
          Depending on where you live, you may have the right to access, correct, delete, or export
          your personal information, and to object to certain processing. To exercise any of these
          rights, email{" "}
          <a className="text-indigo-300 hover:text-indigo-200" href="mailto:privacy@equallayer.app">
            privacy@equallayer.app
          </a>{" "}
          and we'll respond within 30 days.
        </p>
      </Section>

      <Section title="6. Cookies">
        <p>
          When you visit equallayer.app, a small banner lets you accept all cookies or essential
          only. Your preference is stored locally in your browser. You can clear that preference at
          any time by clearing your browser's storage for this site.
        </p>
      </Section>

      <Section title="7. Data retention">
        <p>
          We keep your waitlist email until you ask us to remove it, or until Equal ends the early
          access program — whichever comes first. Analytics data is retained in aggregate form.
        </p>
      </Section>

      <Section title="8. Security">
        <p>
          We use industry-standard measures to protect your data. No system is perfectly secure, but
          we treat every breach seriously and will notify affected users promptly if one occurs.
        </p>
      </Section>

      <Section title="9. Children">
        <p>
          Equal is not directed to children under 13 (or the minimum digital consent age in your
          region). We do not knowingly collect information from children.
        </p>
      </Section>

      <Section title="10. Changes to this policy">
        <p>
          We may update this policy from time to time. When we do, we'll update the date above and
          — for material changes — notify early-access users by email.
        </p>
      </Section>

      <Section title="11. Contact">
        <p>
          Questions about this policy, or about how Equal handles your data? Email{" "}
          <a className="text-indigo-300 hover:text-indigo-200" href="mailto:privacy@equallayer.app">
            privacy@equallayer.app
          </a>
          .
        </p>
      </Section>
    </LegalShell>
  );
};

export default Privacy;
